/**
 * Race-live coach map — the VIEW MODEL. Map lane step 2 (2026-08-06), built
 * to CD 2026-08-05 R31/R32/R34 and the MAP-1..11 strings (C25 ✓ founder).
 *
 * Everything here is pure: feed-state derivation, the signed strings, age
 * math, ordering law, the departed-set diff. The route renders what this
 * module says and adds nothing — so the R32 vocabulary and the §4.2 ordering
 * rules are testable without MapKit or a DOM.
 *
 * ⚖️ The ruled vocabulary (R32), verbatim:
 *   · LIVE — fresh heartbeat, moving. Bare dot.
 *   · STOPPED — fresh heartbeat, position unchanged — MEASURED (the server's
 *     `stoppedSince`), never inferred. Chip: `Stopped · {n} min`.
 *   · STALE — no heartbeat for 30 s. Dot fades to 50 %, chip `Last fix ·
 *     {age}` — a measurement, never "stopped", never a verdict.
 *   · OFF THE MAP — the feed is ABSENT from the poll (suspension beyond the
 *     liveness window, a kill, or expiry). Marker + card gone within one poll
 *     cycle, no reason code. The client cannot and must not distinguish why.
 *
 * ⛔ §4.2 THE RANKING FENCE: nothing in this module orders by position, pace
 * or place. The sharing list is FIXED ALPHABETICAL; annotation z-order is
 * stable athlete-id insertion (R31) — never geometry, never pace.
 * ⛔ NO entered-count anywhere: "six of nine" is the refusal roster, and the
 * map never does that arithmetic (R34).
 */

/** One row of GET /api/race-live/positions — the §4.3 coach poll. */
export interface RaceLivePositionRow {
  athleteId: string;
  grantId: string;
  lat: number;
  lng: number;
  elevationM: number | null;
  accuracyM: number | null;
  distanceM: number | null;
  paceSPerKm: number | null;
  /** ⚖️ REVIEW F5 — the only honest last-fix age. */
  deviceTs: string | null;
  receivedAt: string | null;
  lastSeenAt: string | null;
  stoppedSince: string | null;
  /** Grant arm time — MAP-9 `Sharing since {time}`. */
  armedAt: string | null;
}

export type FeedState = 'live' | 'stopped' | 'stale';

export interface FeedView {
  athleteId: string;
  /** Full name — coach-only chrome territory (list + tap card), NEVER on the map. */
  name: string;
  /** Two-letter initials — the only identity the dot itself carries (R31). */
  initials: string;
  state: FeedState;
  lat: number;
  lng: number;
  /** Seconds since the GPS last produced a fix (deviceTs), null if unknown. */
  fixAgeS: number | null;
  /** Whole minutes stopped (floor), null unless state is 'stopped'. */
  stoppedMin: number | null;
  paceSPerKm: number | null;
  distanceM: number | null;
  elevationM: number | null;
  armedAt: string | null;
}

/** R32 — silence threshold where a feed reads STALE and on-map age begins. */
export const STALE_AFTER_MS = 30_000;

/** Builder default (Rung ② §4.3): the coach client polls every 10 s. */
export const POLL_INTERVAL_MS = 10_000;

/** Header flips to MAP-3 (— reconnecting) after two missed cycles. */
export const POLL_DEGRADED_AFTER_MS = 2 * POLL_INTERVAL_MS;

/** MAP-6 tray line is transient: next tap or 60 s (R34). */
export const TRAY_TRANSIENT_MS = 60_000;

/**
 * R31 — two-letter initials from a display name. First letter of the first
 * and last words; single-word names double their first letter's word start.
 * Deterministic and locale-naive on purpose — the same name always renders
 * the same dot.
 */
export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '··';
  const first = words[0][0] || '·';
  const last = words.length > 1 ? words[words.length - 1][0] || '·' : words[0][1] || first;
  return (first + last).toUpperCase();
}

/**
 * R32 — the feed state. STALE is measured from the HEARTBEAT (`lastSeenAt`):
 * 30 s of silence, strictly before the server's liveness window would remove
 * the feed entirely. STOPPED is the server's own measurement (`stoppedSince`)
 * — never inferred here from coordinates.
 */
export function deriveFeedState(
  row: Pick<RaceLivePositionRow, 'lastSeenAt' | 'stoppedSince'>,
  nowMs: number,
): FeedState {
  const seen = row.lastSeenAt ? Date.parse(row.lastSeenAt) : NaN;
  if (!Number.isFinite(seen) || nowMs - seen >= STALE_AFTER_MS) return 'stale';
  return row.stoppedSince ? 'stopped' : 'live';
}

/** Seconds since the GPS fix (`deviceTs`); null when the fix time is unknown. */
export function fixAgeSeconds(deviceTs: string | null, nowMs: number): number | null {
  if (!deviceTs) return null;
  const t = Date.parse(deviceTs);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 1000));
}

/** `45 s` · `3 min` · `1 h 12 min` — the age grammar for MAP-5 / MAP-9. */
export function formatFixAge(ageS: number): string {
  if (ageS < 60) return `${ageS} s`;
  const min = Math.floor(ageS / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${h} h` : `${h} h ${rem} min`;
}

/** Build one FeedView from a poll row + the roster name. */
export function toFeedView(
  row: RaceLivePositionRow,
  name: string,
  nowMs: number,
): FeedView {
  const state = deriveFeedState(row, nowMs);
  const stoppedAt = row.stoppedSince ? Date.parse(row.stoppedSince) : NaN;
  return {
    athleteId: row.athleteId,
    name,
    initials: initialsFor(name),
    state,
    lat: row.lat,
    lng: row.lng,
    fixAgeS: fixAgeSeconds(row.deviceTs, nowMs),
    stoppedMin:
      state === 'stopped' && Number.isFinite(stoppedAt)
        ? Math.floor((nowMs - stoppedAt) / 60_000)
        : null,
    paceSPerKm: row.paceSPerKm,
    distanceM: row.distanceM,
    elevationM: row.elevationM,
    armedAt: row.armedAt,
  };
}

/* ── The signed strings (CD 2026-08-05 §2 · C25 ✓ founder). Ship
      character-for-character; no string below may be edited outside a CD
      session + founder sign-off. ───────────────────────────────────── */

/** MAP-1 — header lock-up. Yellow ● LIVE only with ≥1 FRESH feed (R34);
 *  empty is the quiet `0 sharing`, no dot. A nonempty set with zero fresh
 *  feeds keeps the count and drops the LIVE claim with the dot — the header
 *  never asserts LIVE the data cannot support.
 *
 *  MAP-1a (R54, ratified amendment to MAP-1's empty case): while athletes are
 *  ARMED but nothing has yet been disclosed this viewing session, the empty
 *  form amends to the quiet `{n} armed` — count only, no dot, no names. After
 *  the first disclosure the ratified forms own the header again (a departed
 *  feed must not read as "armed" — the revoke stays unreadable). */
export function headerLockup(
  views: ReadonlyArray<Pick<FeedView, 'state'>>,
  armed?: { armedCount: number; disclosureSeen: boolean },
): {
  text: string;
  liveDot: boolean;
} {
  const n = views.length;
  const fresh = views.some((v) => v.state !== 'stale');
  if (n > 0 && fresh) return { text: `LIVE · ${n} sharing`, liveDot: true };
  if (n > 0) return { text: `${n} sharing`, liveDot: false };
  if (armed && armed.armedCount > 0 && !armed.disclosureSeen) {
    return { text: `${armed.armedCount} armed`, liveDot: false };
  }
  return { text: '0 sharing', liveDot: false };
}

/**
 * R51 — the header meta line: `{date} · {course}`, course omitted when the
 * meet has none. The date is NEW in the header (race-page pass); parsed from
 * the meet's date-only string with LOCAL date parts — `new Date('YYYY-MM-DD')`
 * is UTC midnight and renders the previous day west of Greenwich.
 */
export function formatMeetDateShort(meetDateIso: string | null): string | null {
  if (!meetDateIso) return null;
  const [y, m, d] = meetDateIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function headerMetaLine(
  meetDateIso: string | null,
  courseName: string | null,
): string | null {
  const date = formatMeetDateShort(meetDateIso);
  if (!date) return courseName || null;
  return courseName ? `${date} · ${courseName}` : date;
}

/**
 * R54 — the tray's heading slot, one decision in one place: the armed ladder's
 * middle rung renders MAP-12 IN the label slot (never `SHARING · 0` above it);
 * every other state keeps MAP-11. Count only — no names before the first
 * disclosed fix.
 */
export function trayHeading(args: {
  sharingCount: number;
  armedCount: number;
  disclosureSeen: boolean;
}): { kind: 'sharing' | 'armed'; text: string } {
  if (args.sharingCount === 0 && args.armedCount > 0 && !args.disclosureSeen) {
    return { kind: 'armed', text: armedTrayLine(args.armedCount) };
  }
  return { kind: 'sharing', text: trayLabel(args.sharingCount) };
}

/** MAP-2 / MAP-3 — poll-age line. Degraded (≥2 missed cycles) renders the
 *  reconnecting form in `--warn`. */
export function pollAgeLine(lastOkMs: number | null, nowMs: number): {
  text: string;
  degraded: boolean;
} {
  if (lastOkMs === null) return { text: 'connecting…', degraded: false };
  const ageS = Math.max(0, Math.floor((nowMs - lastOkMs) / 1000));
  if (nowMs - lastOkMs >= POLL_DEGRADED_AFTER_MS) {
    return { text: `last update ${ageS} s ago — reconnecting`, degraded: true };
  }
  return { text: `updated ${ageS} s ago`, degraded: false };
}

/** MAP-4 / MAP-5 — the on-map chip. R32's progressive disclosure: age is
 *  always in the tap card; ON-MAP it renders from 30 s of silence (which IS
 *  the stale threshold). A stop younger than one minute renders no chip —
 *  the elapsed grammar is whole minutes. */
export function dotChip(view: Pick<FeedView, 'state' | 'stoppedMin' | 'fixAgeS'>): string | null {
  if (view.state === 'stale') {
    return view.fixAgeS === null ? null : `Last fix · ${formatFixAge(view.fixAgeS)}`;
  }
  if (view.state === 'stopped' && view.stoppedMin !== null && view.stoppedMin >= 1) {
    return `Stopped · ${view.stoppedMin} min`;
  }
  return null;
}

/**
 * ⚖️ R68 (CD 2026-08-22) — THE CHIP NEVER SUPPRESSES ON COLLISION.
 *
 * R32's dense-fan rule ("never one chip over many dots") is SUPERSEDED here:
 * a missing chip would read as a missing state, and this surface may not
 * withhold a measurement because two athletes ran close together. Overlap
 * resolves by Z-ORDER ONLY — `stackingOrder` below — which is why the
 * proximity helper (`chipSuppressedIds` / `CHIP_DENSE_THRESHOLD_M`) is gone
 * rather than left dormant: dead law invites re-introduction.
 */

/** MAP-9 — tap-card meta. */
export function tapCardMeta(view: Pick<FeedView, 'armedAt' | 'fixAgeS'>): string {
  const armedMs = view.armedAt ? Date.parse(view.armedAt) : NaN;
  const since = Number.isFinite(armedMs)
    ? new Date(armedMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '—';
  const age = view.fixAgeS === null ? '—' : formatFixAge(view.fixAgeS);
  return `Sharing since ${since} · last fix ${age} ago`;
}

/** MAP-10 — tap-card footer. */
export function tapCardFooter(name: string): string {
  return `Live while ${name} shares. No history is kept on this map.`;
}

/** MAP-6 — the transient tray line for feeds that left the map. Initials
 *  only — a departed feed's NAME never renders here (the card is gone within
 *  one poll cycle and the tray must not resurrect identity). */
export function departedTrayLine(initials: ReadonlyArray<string>): string {
  return `${initials.join(', ')} left the map. A feed ends when sharing ends or signal drops — the map doesn't say which.`;
}

/** MAP-6's permanent form behind ⓘ — the explainer clause without any feed. */
export const OFF_THE_MAP_EXPLAINER =
  "A feed ends when sharing ends or signal drops — the map doesn't say which.";

/** MAP-7 / MAP-8 — the empty state. */
export const EMPTY_PILL = 'No one is sharing yet.';
export const EMPTY_TRAY = 'Dots appear when an athlete shares. Each athlete chooses, race by race.';

/**
 * B10 (2026-08-20) — the honest pre-race tray line: athletes ARMED, nothing
 * disclosed yet. A coach opening the map before the start read a bare
 * `SHARING · 0` + MAP-8 and took the plumbing for broken (founder + his
 * daughter, live, 2026-08-20). Renders ONLY while no feed has yet been
 * disclosed in this viewing session — once dots have appeared and left,
 * MAP-6's deliberate ambiguity ("…the map doesn't say which") owns the
 * empty state again, so a revoke stays unreadable from the tray.
 *
 * ✅ MAP-12, RATIFIED verbatim (C30, race-page pass §2). Register: count of
 * CONSENTS, not a roster ("armed" ≠ entered — no R34 refusal arithmetic is
 * possible from it).
 */
export function armedTrayLine(n: number): string {
  return `${n} armed · not yet visible`;
}

/** MAP-13 — tray explainer beneath MAP-12, armed-&-none-visible state only
 *  (C30, race-page pass §2 — ratified; the map surface keeps its em dashes,
 *  R49 was scoped to §D1). */
export const ARMED_TRAY_EXPLAINER =
  'A dot appears a short way into each run — the first 200 m never show.';

/** MAP-11 — tray label. */
export function trayLabel(n: number): string {
  return `SHARING · ${n}`;
}

/** FUN-C's three norms — the full-screen first-use gate (R39, step 3) and
 *  the persistent ⓘ sheet (R34) carry the same three, verbatim. */
export const MAP_NORMS: ReadonlyArray<string> = [
  "Sharing is each athlete's choice, made race by race.",
  "No dot needs no explanation. Don't ask an athlete why they aren't sharing.",
  'This map is not attendance. A missing dot never means a missing athlete.',
];

/** FUN-C — the rest of the gate's signed strings (R39: the copy IS the
 *  control; acknowledgment required; the map does not render behind it). */
export const FUN_C_TITLE = 'Three things about this map.';
export const FUN_C_ACK = 'I understand';
export const FUN_C_FOOTER = 'These stay one tap away — ⓘ on the map.';
/** R50 — the light gate's kicker (race-page pass, frame 1b). */
export const FUN_C_KICKER = 'COACH LIVE MAP · FIRST OPEN';

/** R39 — per-coach acknowledgment key (device-scoped: a new device re-shows
 *  the gate, deliberately — norms bear repeating on a fresh screen). */
export function normsAckStorageKey(coachUserId: string): string {
  return `race_live_norms_ack:${coachUserId}`;
}

/* ── Ordering law ─────────────────────────────────────────────────── */

/** R34 — the sharing list is FIXED ALPHABETICAL (name, then athlete-id for
 *  identical names). Never geometric, never pace. */
export function sharingListOrder(views: ReadonlyArray<FeedView>): FeedView[] {
  return [...views].sort(
    (a, b) => a.name.localeCompare(b.name) || a.athleteId.localeCompare(b.athleteId),
  );
}

/**
 * ⚖️ R68 §4(c) — DOT + CHIP Z-STACKING: SELECTION FIRST, THEN ALPHABETICAL.
 *
 * Declared latitude on R31's athlete-id insertion, and the only order this
 * surface already trusts: the selected dot and its chip sit above everything,
 * and everyone else keeps the ordering law's own alphabetical order. ⛔ A
 * stacking order is not a hierarchy of athletes — it is never geometry, never
 * pace, and it carries no ranking.
 *
 * Returned in PAINT order (bottom → top), so the selected athlete is LAST:
 * the caller adds annotations in this order and later annotations draw over
 * earlier ones. The order depends only on names, ids and the selection, so it
 * is STABLE ACROSS POLLS — a tick can never restack anything, and nothing
 * flickers when a fix lands.
 */
export function stackingOrder(
  views: ReadonlyArray<FeedView>,
  selectedId: string | null,
): FeedView[] {
  const alphabetical = sharingListOrder(views);
  return [
    ...alphabetical.filter((v) => v.athleteId !== selectedId),
    ...alphabetical.filter((v) => v.athleteId === selectedId),
  ];
}

/** OFF-THE-MAP diff — athletes present last poll and absent now. */
export function departedIds(
  prev: ReadonlySet<string>,
  current: ReadonlySet<string>,
): string[] {
  return [...prev].filter((id) => !current.has(id)).sort();
}

/**
 * Clock-skew estimate: server stamps (`receivedAt`) vs the client clock. A
 * coach phone running 30 s fast would otherwise read every feed as STALE; a
 * slow one would never see STALE. The freshest `receivedAt` is at most one
 * flush old, so `now − max(receivedAt)` ≈ skew (plus a bounded freshness
 * error that only ever DELAYS a stale verdict, never invents one). Null when
 * the poll carried no usable stamp — caller keeps its previous estimate.
 */
export function estimateClockSkewMs(
  rows: ReadonlyArray<Pick<RaceLivePositionRow, 'receivedAt'>>,
  nowMs: number,
): number | null {
  let freshest = -Infinity;
  for (const r of rows) {
    const t = r.receivedAt ? Date.parse(r.receivedAt) : NaN;
    if (Number.isFinite(t) && t > freshest) freshest = t;
  }
  if (!Number.isFinite(freshest)) return null;
  return nowMs - freshest;
}

/* ── ⚖️ R69 · THE MOTION MODEL — the pure half (CD 2026-08-22) ────────
      Option (b): between two CONSECUTIVELY RECEIVED fixes the dot glides
      LINEARLY AT CONSTANT SPEED over the MEASURED inter-fix interval, so it
      sits about one poll behind the newest fix instead of being frozen for
      nine of every ten seconds.

      Linear, not eased: an ease invents acceleration the data does not carry,
      and a constant-speed line between two known points is the most honest
      curve available.

      ⛔ NEVER EXTRAPOLATE. Interpolation may not run past the newest fix. If
      the next fix has not landed by the render cap the dot HOLDS and MAP-2/3
      poll age — not the dot — carries the degradation.

      ⛔ NEVER BRIDGE A GAP. Across a missed-fix window the dot does not
      animate at all: it re-seats in ONE FRAME, a cut and not a move, and the
      MAP-5 chip plus the shipped 50 % dim carry the explanation. The wake
      breaks at the same seam (R66's WAKE_GAP_MS is the same 30 s as
      STALE_AFTER_MS below — one silence, one seam).

      No new string: this module adds none, and moves none. ────────────── */

/**
 * R69 — the RENDER cap. §4(g): a RENDER cap, never a liveness threshold.
 * Server liveness machinery, the 2 h cap and the stopped-vs-stale law are
 * untouched by anything in this section.
 */
export const INTERPOLATION_RENDER_CAP_MS = 12_000;

/** One fix, as the motion model needs it. */
export interface MotionFix {
  lat: number;
  lng: number;
  /** The fix's OWN device time (ms) — the measured interval comes from these,
   *  never from receipt time, which carries network weather. */
  tMs: number;
  /** The applied-poll index that carried this fix — consecutive-fix
   *  eligibility. An athlete absent from a poll breaks the run. */
  seq: number;
}

/** A permitted glide. `ms` is the measured interval, capped for render. */
export interface TweenSegment {
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
  ms: number;
}

/**
 * The eligibility decision, in one place. Returns `null` when a tween is NOT
 * permitted — the caller then SEATS the fix in one frame, which is the honest
 * render for every case below:
 *
 *   · no previous fix — the first fix seats; there is nothing to move from;
 *   · non-consecutive polls — the athlete was absent in between, so the path
 *     between the two fixes is unknown and may never be drawn;
 *   · a device-time gap wider than the stale seam — the same silence that
 *     fades the dot and raises the MAP-5 chip;
 *   · no forward interval at all — a re-served fix is not movement.
 */
export function tweenSegment(prev: MotionFix | null, next: MotionFix): TweenSegment | null {
  if (!prev) return null;
  if (next.seq !== prev.seq + 1) return null;
  const interval = next.tMs - prev.tMs;
  if (!Number.isFinite(interval) || interval <= 0) return null;
  // ⛔ the missed-fix window: a cut, not a move.
  if (interval > STALE_AFTER_MS) return null;
  return {
    from: { lat: prev.lat, lng: prev.lng },
    to: { lat: next.lat, lng: next.lng },
    ms: Math.min(interval, INTERPOLATION_RENDER_CAP_MS),
  };
}

/**
 * How far along a glide is at `elapsedMs`. Clamped to 1 — THIS is the clause
 * that makes extrapolation structurally impossible: the fraction can never
 * exceed 1, so the dot can never pass the newest fix, whatever the clock does.
 */
export function tweenFraction(elapsedMs: number, durationMs: number): number {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return 1;
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  return Math.min(1, elapsedMs / durationMs);
}

/** The point at a fraction — linear in both axes, constant speed. */
export function tweenAt(seg: TweenSegment, fraction: number): { lat: number; lng: number } {
  const f = Math.max(0, Math.min(1, fraction));
  return {
    lat: seg.from.lat + (seg.to.lat - seg.from.lat) * f,
    lng: seg.from.lng + (seg.to.lng - seg.from.lng) * f,
  };
}

/* ── EC-0 basics formatting (tap card: pace · distance · elevation) ── */

const METERS_PER_MILE = 1609.344;

/**
 * ⚖️ B24 — THE PLAUSIBILITY FLOOR (founder direction, docket 2026-08-26).
 *
 * A tray row rendered `121:55 /mi` in the field. That number is not a pace: the
 * server derives pace from the device's own `speed` and guards only `speed > 0`
 * (`api/lib/run/raceLiveChannel.js`), so 0.22 m/s of standing GPS wander becomes
 * ~4536 s/km and the tray reads it out verbatim.
 *
 * A number a coach cannot act on is worse than no number, because it invites
 * being acted on. Past this floor the tray says `—`, which is the SAME grammar
 * the athlete side has always used: `lib/run/geo.ts`'s `formatPace` dashes past
 * 60:00 /mi, and `rollingPaceSecPerMile` refuses to report a pace at all below
 * 35 m of movement per window. This is the coach-side twin of both.
 *
 * 20:00 /mi is WALKING-GENEROUS on purpose: a brisk walk is ~15:00 and an easy
 * one ~18:00, so a genuinely walking athlete still reads out. It is one line to
 * tune — and it is the line to tune if an injured athlete walking a course in
 * slower than 20:00 ever needs her pace shown.
 *
 * ⛔ NOT a staleness rule. R52's law — a STALE row dims to 60 % and KEEPS its
 * last served numbers — is untouched: this keys off the pace MAGNITUDE alone
 * and never off `state`, so a stale row with a plausible pace still shows it,
 * and a LIVE row with an implausible one still dashes.
 */
export const IMPLAUSIBLE_PACE_S_PER_MI = 20 * 60;

export function formatPace(paceSPerKm: number | null): string {
  if (paceSPerKm === null || !Number.isFinite(paceSPerKm) || paceSPerKm <= 0) return '—';
  const sPerMi = paceSPerKm * (METERS_PER_MILE / 1000);
  // B24 — the floor. Same U+2014 em dash the null case returns, because the
  // route styles a held slot by comparing against that exact string.
  if (sPerMi > IMPLAUSIBLE_PACE_S_PER_MI) return '—';
  const min = Math.floor(sPerMi / 60);
  const sec = Math.round(sPerMi % 60);
  const s = sec === 60 ? 0 : sec;
  const m = sec === 60 ? min + 1 : min;
  return `${m}:${String(s).padStart(2, '0')} /mi`;
}

export function formatDistance(distanceM: number | null): string {
  if (distanceM === null || !Number.isFinite(distanceM) || distanceM < 0) return '—';
  return `${(distanceM / METERS_PER_MILE).toFixed(1)} mi`;
}

export function formatElevation(elevationM: number | null): string {
  if (elevationM === null || !Number.isFinite(elevationM)) return '—';
  return `${Math.round(elevationM * 3.28084)} ft`;
}
